import {ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {ITEM_TYPE, TouchItemData} from "../../../screen-layout.data";
import {MetaTagType} from "../../../../../catalogue/meta-tag/meta-tag.data";
import {ColorEditControlComponent} from "../../../../../framework/common-controls/edit-controls/color-edit-control/color-edit-control.component";
import {ScreenLayoutEditorModel} from "../screen-layout-editor.model";
import {Subscription} from "rxjs";
import {ScreenLayoutEntityModel} from "../../../screen-layout-entity.model";
import {EntityFormBuilder} from "../../../../../framework/entity/edit/entity-form-builder/entity-form-builder";
import {FormGroup} from "@angular/forms";
import {EditService} from "../../../../../framework/entity/edit/edit-page/edit.service";

/**
 * Properties for a single touch item on the menu
 */
@Component({
    selector: "wbo-screen-layout-item-properties",
    templateUrl: "./screen-layout-item-properties.component.html",
    styleUrls: ["./screen-layout-item-properties.component.scss"]
})
export class ScreenLayoutItemPropertiesComponent implements OnInit, OnDestroy {
    ITEM_TYPE = ITEM_TYPE;
    MetaTagType = MetaTagType;

    private subscriptions: Subscription = new Subscription();

    item: TouchItemData;

    formGroup: FormGroup;

    itemBlank: boolean;

    itemTypeReadonly: boolean;

    setToUndefined: boolean;

    courseDefinitionId: number;

    constructor(public model: ScreenLayoutEditorModel,
                public entityModel: ScreenLayoutEntityModel,
                private entityFormBuilder: EntityFormBuilder,
                private changeDetectorRef: ChangeDetectorRef,
                private editService: EditService) {

    }

    ngOnInit(): void {
        this.formGroup = this.entityFormBuilder.groupCollection(this.entityModel, "configCategories.touchItems", 0);

        this.subscriptions.add(this.editService.$entityRestricted.subscribe(restricted => {
            if (restricted) {
                this.editService.restrictAllControls(this.formGroup);
            }
        }));

        this.subscriptions.add(this.model.selectedItem$.subscribe((value) => {
            // it is setting to setToUndefined to avoid destroying the view as the ColourPicker component is doing a setTimeout with detectChanges that can cause an error
            // detectChanges on destroyed view
            if (this.item && !value) {
                this.setToUndefined = true;
            } else {
                this.item = value;
                this.setToUndefined = false;

                if (this.item && this.formGroup) {
                    this.formGroup.markAsPristine();
                }

                this.itemBlank = this.item && this.item.itemType === ITEM_TYPE.BLANK;

                this.itemTypeReadonly = this.item && this.item.itemType === ITEM_TYPE.SUB_MENU;

                this.changeDetectorRef.detectChanges();
            }
        }));

        this.subscriptions.add(this.model.courseDefinitionId$.subscribe((value) => {
            this.courseDefinitionId = value;
            this.changeDetectorRef.detectChanges();
        }));

        const thiz = this;
        this.model.registerRecordValidator(() => this.validateForm.call(thiz));
    }

    validateForm(): Promise<boolean> {
        if (this.setToUndefined || !this.item || !this.formGroup.dirty) {
            return Promise.resolve(true)
        } else {
            if (!this.formGroup.valid) {
                this.entityFormBuilder.markControlsAsTouched(this.formGroup);
            }
            return Promise.resolve(this.formGroup.valid)
        }
    }

    get showExtraRules(): boolean {
        return this.item.itemType == ITEM_TYPE.EXTRA;
    }

    get showPopup(): boolean {
        return this.item.itemType == ITEM_TYPE.SELLABLE || this.item.itemType == ITEM_TYPE.EXTRA || this.item.itemType == ITEM_TYPE.PREP || this.item.itemType == ITEM_TYPE.SIZE;
    }

    get identifier(): number {
        return this.item.identifier;
    }

    set identifier(value: number) {
        this.item.identifier = value;
    }

    get displayName(): string {
        let name: string = this.item.displayName;
        if (name) {
            name = name.replace(/<br\s*\/?>/mg, "\n");
        }
        return name;
    }

    set displayName(value: string) {
        if (value) {
            value = value.replace(/(?:\r\n|\r|\n)/g, "<br>");
        }
        this.item.displayName = value;
    }

    get colour(): string {
        return ColorEditControlComponent.toColorHex(this.item.colour);
    }

    set colour(value: string) {
        this.item.colour = ColorEditControlComponent.fromColorHex(value);
    }

    get foreColour(): string {
        return ColorEditControlComponent.toColorHex(this.item.foreColour);
    }

    set foreColour(value: string) {
        this.item.foreColour = ColorEditControlComponent.fromColorHex(value);
    }

    get applyExtraRules(): boolean {
        return this.item.applyExtraRules;
    }

    set applyExtraRules(value: boolean) {
        this.item.applyExtraRules = value;
    }

    get popup(): boolean {
        return this.item.popup;
    }

    set popup(value: boolean) {
        this.item.popup = value;
    }

    get sellableSizeGroup(): number {
        return this.item.sellableSizeGroup;
    }

    set sellableSizeGroup(value: number) {
        this.item.sellableSizeGroup = value;
    }

    get itemType(): ITEM_TYPE {
        return this.item.itemType === ITEM_TYPE.BLANK ? undefined : this.item.itemType;
    }

    set itemType(value: ITEM_TYPE) {
        if (this.item.itemType !== value) {
            this.item.itemType = value == undefined ? ITEM_TYPE.BLANK : value;
            this.item.identifier = value === ITEM_TYPE.SUB_MENU ? 0 : undefined;

            if (value === ITEM_TYPE.EXTRA) {
                this.item.applyExtraRules = true;
            } else if (value == ITEM_TYPE.SUB_MENU) {
                this.item.assign({
                    gridColumns: ScreenLayoutEditorModel.DEFAULT_COLUMNS,
                    gridRows: ScreenLayoutEditorModel.DEFAULT_ROWS,
                });
            }

            if (this.itemBlank && value != undefined && value != ITEM_TYPE.BLANK) {
                this.itemBlank = false;
                this.model.addItem(this.item);
            }
        }
    }

    get courseId(): number {
        return this.item.courseId;
    }

    set courseId(value: number) {
        this.item.courseId = value;
    }

    changeSellable(data: any): void {
        this.item.displayName = data.name;
        this.item.icon = undefined;
        this.item.iconCheckRequired = true;
        this.entityModel.loadIconIfRequired(this.item);
    }

    changeExtra(data: any): void {
        this.item.displayName = data.name;
        this.item.icon = undefined;
        this.item.iconCheckRequired = true;
        this.entityModel.loadIconIfRequired(this.item);
    }

    changePreparationModifier(data: any): void {
        this.item.displayName = data.metaTagValueName;
    }

    changeSize(data: any): void {
        this.item.displayName = data.valueName;
    }

    changeFunction(data: any): void {
        if (!this.item.displayName) {
            this.item.displayName = data.label;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();

        this.model.unregisterRecordValidator(this.validateForm);
    }
}
